import { useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import {
    Control,
    FieldErrors,
    UseFormRegister,
    useFieldArray,
    useWatch,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
import Input from 'src/components/FormComponents/Input';
import {
    OrganizationFormValues,
    OrganizationGroupFormValue,
    OrganizationHierarchyFormValue,
} from './createOrganizationForm.schema';
import { CaretIcon, PlusIcon } from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';

interface GroupsSectionProps {
    control: Control<OrganizationFormValues>;
    register: UseFormRegister<OrganizationFormValues>;
    errors: FieldErrors<OrganizationFormValues>;
    t: TFunction;
    groups: OrganizationGroupFormValue[];
    readOnly?: boolean;
}

export const OrganizationGroupsSection = ({
    control,
    register,
    errors,
    t,
    groups,
    readOnly = false,
}: GroupsSectionProps) => {
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'groups',
    });
    const watchedGroups = useWatch({
        control,
        name: 'groups',
    }) as OrganizationGroupFormValue[] | undefined;

    const watchedHierarchy = useWatch({
        control,
        name: 'hierarchy',
    }) as OrganizationHierarchyFormValue[] | undefined;

    const buildGroupValue = (index: number): OrganizationGroupFormValue => {
        const watched = watchedGroups?.[index];
        const fallback = groups?.[index];
        return (
            watched ??
            fallback ?? {
                id: fields[index]?.id,
                name: '',
                parentId: undefined,
            }
        );
    };

    const groupedEntries = useMemo(() => {
        return fields.map((field, index) => {
            const value = buildGroupValue(index);
            return {
                field,
                index,
                value,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, watchedGroups]);

    const childrenLookup = useMemo(() => {
        const map = new Map<string, typeof groupedEntries>();
        groupedEntries.forEach(entry => {
            const parentId = entry.value.parentId;
            if (!parentId) {
                return;
            }
            const current = map.get(parentId) ?? [];
            current.push(entry);
            map.set(parentId, current);
        });
        return map;
    }, [groupedEntries]);

    const collectDescendantIndices = (groupId?: string): number[] => {
        if (!groupId) return [];
        const children = childrenLookup.get(groupId) ?? [];
        const childIndices = children.map(child => child.index);
        children.forEach(child => {
            childIndices.push(
                ...collectDescendantIndices(child.value.id ?? child.field.id),
            );
        });
        return childIndices;
    };

    const rootNodes = useMemo(() => {
        return groupedEntries.filter(entry => !entry.value.parentId);
    }, [groupedEntries]);

    const normalizedHierarchy = useMemo(() => {
        const source = watchedHierarchy ?? [];
        return source
            .map(level => ({
                ...level,
                name: level.name?.trim() ?? '',
                parentId: level.parentId?.trim()
                    ? level.parentId.trim()
                    : undefined,
            }))
            .filter(level => level.name.length > 0);
    }, [watchedHierarchy]);

    const maxHierarchyDepth = useMemo(() => {
        if (normalizedHierarchy.length === 0) {
            return 0;
        }

        const byParentId = new Map<string, OrganizationHierarchyFormValue[]>();
        normalizedHierarchy.forEach(level => {
            const parentKey = level.parentId ?? '';
            const current = byParentId.get(parentKey) ?? [];
            current.push(level);
            byParentId.set(parentKey, current);
        });

        const calcDepth = (parentId: string, visited: Set<string>): number => {
            const children = byParentId.get(parentId) ?? [];
            if (children.length === 0) {
                return 0;
            }
            return (
                1 +
                Math.max(
                    ...children.map(child => {
                        if (visited.has(child.id)) {
                            return 0;
                        }
                        const nextVisited = new Set(visited);
                        nextVisited.add(child.id);
                        return calcDepth(child.id, nextVisited);
                    }),
                )
            );
        };

        return calcDepth('', new Set());
    }, [normalizedHierarchy]);

    const hierarchyChildrenByParentId = useMemo(() => {
        const byParentId = new Map<string, OrganizationHierarchyFormValue[]>();
        normalizedHierarchy.forEach(level => {
            const parentKey = level.parentId ?? '';
            const current = byParentId.get(parentKey) ?? [];
            current.push(level);
            byParentId.set(parentKey, current);
        });

        for (const [parentId, children] of byParentId) {
            children.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
            byParentId.set(parentId, children);
        }

        return byParentId;
    }, [normalizedHierarchy]);

    const hierarchyById = useMemo(() => {
        const map = new Map<string, OrganizationHierarchyFormValue>();
        normalizedHierarchy.forEach(level => map.set(level.id, level));
        return map;
    }, [normalizedHierarchy]);

    const defaultHierarchyLevelByDepth = useMemo(() => {
        const result = new Map<number, OrganizationHierarchyFormValue>();

        const visit = (
            level: OrganizationHierarchyFormValue,
            depth: number,
            visited: Set<string>,
        ) => {
            if (visited.has(level.id)) {
                return;
            }

            if (!result.has(depth)) {
                result.set(depth, level);
            }

            const nextVisited = new Set(visited);
            nextVisited.add(level.id);

            const children = hierarchyChildrenByParentId.get(level.id) ?? [];
            children.forEach(child => visit(child, depth + 1, nextVisited));
        };

        const roots = hierarchyChildrenByParentId.get('') ?? [];
        roots.forEach(root => visit(root, 0, new Set()));
        return result;
    }, [hierarchyChildrenByParentId]);

    const getDefaultLevelIdFor = (
        depth: number,
        parentLevelId?: string,
    ): string | undefined => {
        if (parentLevelId) {
            const candidates = hierarchyChildrenByParentId.get(parentLevelId);
            if (candidates?.length) {
                return candidates[0].id;
            }
        }

        return defaultHierarchyLevelByDepth.get(depth)?.id;
    };

    const handleAddGroup = (options?: {
        parentId?: string;
        depth?: number;
        levelId?: string;
        parentLevelId?: string;
    }) => {
        const depth = options?.depth ?? 0;
        const levelId =
            options?.levelId ??
            getDefaultLevelIdFor(depth, options?.parentLevelId);

        append({
            id: uuidv4(),
            name: '',
            parentId: options?.parentId,
            levelId: levelId ?? '',
        });
    };

    useEffect(() => {
        if (normalizedHierarchy.length === 0) {
            return;
        }

        const entryByGroupId = new Map<string, typeof groupedEntries[number]>();
        groupedEntries.forEach(entry =>
            entryByGroupId.set(entry.value.id, entry),
        );

        const getDepth = (entry: typeof groupedEntries[number]): number => {
            let depth = 0;
            let current = entry;
            const visited = new Set<string>();
            while (current.value.parentId) {
                if (visited.has(current.value.id)) {
                    break;
                }
                visited.add(current.value.id);

                const parent = entryByGroupId.get(current.value.parentId);
                if (!parent) {
                    break;
                }
                depth += 1;
                current = parent;
            }
            return depth;
        };

        const entriesSortedByDepth = [...groupedEntries].sort(
            (a, b) => getDepth(a) - getDepth(b),
        );

        const computedLevelIdByGroupId = new Map<string, string>();

        for (const entry of entriesSortedByDepth) {
            const existingLevelId = entry.value.levelId;
            const isExistingValid =
                Boolean(existingLevelId) &&
                (existingLevelId ? hierarchyById.has(existingLevelId) : false);

            if (isExistingValid && existingLevelId) {
                computedLevelIdByGroupId.set(entry.value.id, existingLevelId);
                continue;
            }

            const depth = getDepth(entry);
            const parentId = entry.value.parentId;
            const parentLevelId = parentId
                ? computedLevelIdByGroupId.get(parentId) ??
                  entryByGroupId.get(parentId)?.value.levelId
                : undefined;

            const computed = getDefaultLevelIdFor(depth, parentLevelId);
            if (!computed) {
                continue;
            }

            computedLevelIdByGroupId.set(entry.value.id, computed);

            update(entry.index, {
                ...entry.value,
                levelId: computed,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedHierarchy, groupedEntries, update]);

    const uniqueHierarchyLevelNameByDepth = useMemo(() => {
        const result = new Map<number, string | undefined>();
        if (normalizedHierarchy.length === 0) {
            return result;
        }

        const byParentId = new Map<string, OrganizationHierarchyFormValue[]>();
        normalizedHierarchy.forEach(level => {
            const parentKey = level.parentId ?? '';
            const current = byParentId.get(parentKey) ?? [];
            current.push(level);
            byParentId.set(parentKey, current);
        });

        const nameSetsByDepth = new Map<number, Set<string>>();

        const visit = (
            level: OrganizationHierarchyFormValue,
            depth: number,
            visited: Set<string>,
        ) => {
            if (visited.has(level.id)) {
                return;
            }

            const currentSet = nameSetsByDepth.get(depth) ?? new Set<string>();
            currentSet.add(level.name);
            nameSetsByDepth.set(depth, currentSet);

            const nextVisited = new Set(visited);
            nextVisited.add(level.id);

            const children = byParentId.get(level.id) ?? [];
            children.forEach(child => visit(child, depth + 1, nextVisited));
        };

        const roots = byParentId.get('') ?? [];
        roots.forEach(root => visit(root, 0, new Set()));

        for (const [depth, names] of nameSetsByDepth) {
            if (names.size === 1) {
                result.set(depth, [...names][0]);
            } else {
                result.set(depth, undefined);
            }
        }

        return result;
    }, [normalizedHierarchy]);

    const uniqueHierarchyLevelByDepth = useMemo(() => {
        const result = new Map<
            number,
            { id: string; name: string } | undefined
        >();
        if (normalizedHierarchy.length === 0) {
            return result;
        }

        const byParentId = new Map<string, OrganizationHierarchyFormValue[]>();
        normalizedHierarchy.forEach(level => {
            const parentKey = level.parentId ?? '';
            const current = byParentId.get(parentKey) ?? [];
            current.push(level);
            byParentId.set(parentKey, current);
        });

        const idSetsByDepth = new Map<number, Set<string>>();
        const nameSetsByDepth = new Map<number, Set<string>>();

        const visit = (
            level: OrganizationHierarchyFormValue,
            depth: number,
            visited: Set<string>,
        ) => {
            if (visited.has(level.id)) {
                return;
            }

            const idSet = idSetsByDepth.get(depth) ?? new Set<string>();
            idSet.add(level.id);
            idSetsByDepth.set(depth, idSet);

            const nameSet = nameSetsByDepth.get(depth) ?? new Set<string>();
            nameSet.add(level.name);
            nameSetsByDepth.set(depth, nameSet);

            const nextVisited = new Set(visited);
            nextVisited.add(level.id);

            const children = byParentId.get(level.id) ?? [];
            children.forEach(child => visit(child, depth + 1, nextVisited));
        };

        const roots = byParentId.get('') ?? [];
        roots.forEach(root => visit(root, 0, new Set()));

        for (const [depth, ids] of idSetsByDepth) {
            const names = nameSetsByDepth.get(depth) ?? new Set<string>();
            if (ids.size === 1 && names.size === 1) {
                result.set(depth, {
                    id: [...ids][0],
                    name: [...names][0],
                });
            } else {
                result.set(depth, undefined);
            }
        }

        return result;
    }, [normalizedHierarchy]);

    const hasHierarchyConstraint = maxHierarchyDepth > 0;

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
        new Set(),
    );
    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(groupId) ? next.delete(groupId) : next.add(groupId);
            return next;
        });
    };

    const renderGroupNode = (
        entry: typeof groupedEntries[number],
        depth = 0,
    ): JSX.Element => {
        const children = childrenLookup.get(entry.value.id ?? '') ?? [];
        const canAddChild = hasHierarchyConstraint
            ? depth + 1 < maxHierarchyDepth
            : false;

        const currentLevel = entry.value.levelId
            ? hierarchyById.get(entry.value.levelId)
            : uniqueHierarchyLevelByDepth.get(depth);

        const currentLevelName = currentLevel?.name;

        const uniqueChildLevel = (() => {
            if (!currentLevel || !('id' in currentLevel)) {
                return undefined;
            }
            const childrenLevels =
                hierarchyChildrenByParentId.get(currentLevel.id) ?? [];
            return childrenLevels.length === 1 ? childrenLevels[0] : undefined;
        })();

        const nextLevelName =
            uniqueChildLevel?.name ??
            uniqueHierarchyLevelNameByDepth.get(depth + 1);

        const addChildLabel =
            hasHierarchyConstraint && nextLevelName
                ? t('admin.forms.createOrganization.addGroupLevelNamed', {
                      levelName: nextLevelName,
                  })
                : t('admin.forms.createOrganization.addGroup');

        const removeLabel =
            hasHierarchyConstraint && currentLevelName
                ? t('admin.forms.createOrganization.removeGroupLevelNamed', {
                      levelName: currentLevelName,
                  })
                : t('admin.forms.createOrganization.removeGroup');

        const groupNameLabel =
            hasHierarchyConstraint && currentLevelName
                ? t('admin.forms.createOrganization.groupLevelNameLabel', {
                      levelName: currentLevelName,
                  })
                : t('admin.forms.createOrganization.groupName');

        const hasChildren = children.length > 0;
        const groupId = entry.value.id ?? entry.field.id;
        const isExpanded = expandedGroups.has(groupId);

        if (!hasChildren) {
            return (
                <div key={entry.field.id}>
                    <div className="rounded-2xl border border-starkWhite bg-white p-4 shadow-sm">
                        <div className="flex gap-3">
                            <Input
                                className="text-input"
                                containerClassName="flex-1"
                                label={groupNameLabel}
                                error={errors.groups?.[entry.index]?.name}
                                readOnly={readOnly}
                                {...register(
                                    `groups.${entry.index}.name` as const,
                                )}
                            />
                        </div>
                        {!readOnly ? (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-mongooseGray">
                                {canAddChild && (
                                    <button
                                        type="button"
                                        className="text-brightOrange underline"
                                        onClick={() =>
                                            handleAddGroup({
                                                parentId: entry.value.id,
                                                depth: depth + 1,
                                                parentLevelId:
                                                    entry.value.levelId ??
                                                    uniqueHierarchyLevelByDepth.get(
                                                        depth,
                                                    )?.id,
                                                levelId:
                                                    uniqueChildLevel?.id ??
                                                    uniqueHierarchyLevelByDepth.get(
                                                        depth + 1,
                                                    )?.id,
                                            })
                                        }
                                    >
                                        {addChildLabel}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="text-venetianRed underline"
                                    onClick={() => {
                                        const descendantIndices =
                                            collectDescendantIndices(groupId);
                                        const toRemove = [
                                            entry.index,
                                            ...descendantIndices,
                                        ].sort((a, b) => b - a);
                                        remove(toRemove);
                                    }}
                                >
                                    {removeLabel}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            );
        }

        return (
            <div key={entry.field.id}>
                <div className="rounded-2xl border border-starkWhite bg-white shadow-sm">
                    <button
                        type="button"
                        onClick={() => toggleGroup(groupId)}
                        className="flex w-full items-center gap-3 p-4 text-left"
                    >
                        <span className="flex-1 font-medium text-woodyBrown truncate">
                            {entry.value.name || groupNameLabel}
                        </span>
                        <span className="text-xs text-mongooseGray whitespace-nowrap">
                            {children.length}{' '}
                            {children.length === 1 ? 'item' : 'items'}
                        </span>
                        <div
                            className={cx(
                                'transition-transform duration-200 text-woodyBrown text-opacity-50',
                                isExpanded ? 'rotate-180' : 'rotate-0',
                            )}
                        >
                            <TextIcon SVG={CaretIcon} />
                        </div>
                    </button>

                    <div
                        className={cx(
                            'overflow-hidden transition-all duration-300',
                            isExpanded
                                ? 'max-h-[5000px] opacity-100'
                                : 'max-h-0 opacity-0',
                        )}
                    >
                        <div className="px-4 pb-4">
                            <div className="flex gap-3">
                                <Input
                                    className="text-input"
                                    containerClassName="flex-1"
                                    label={groupNameLabel}
                                    error={errors.groups?.[entry.index]?.name}
                                    readOnly={readOnly}
                                    {...register(
                                        `groups.${entry.index}.name` as const,
                                    )}
                                />
                            </div>
                            {!readOnly ? (
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-mongooseGray">
                                    {canAddChild && (
                                        <button
                                            type="button"
                                            className="text-brightOrange underline"
                                            onClick={() =>
                                                handleAddGroup({
                                                    parentId: entry.value.id,
                                                    depth: depth + 1,
                                                    parentLevelId:
                                                        entry.value.levelId ??
                                                        uniqueHierarchyLevelByDepth.get(
                                                            depth,
                                                        )?.id,
                                                    levelId:
                                                        uniqueChildLevel?.id ??
                                                        uniqueHierarchyLevelByDepth.get(
                                                            depth + 1,
                                                        )?.id,
                                                })
                                            }
                                        >
                                            {addChildLabel}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="text-venetianRed underline"
                                        onClick={() => {
                                            const descendantIndices =
                                                collectDescendantIndices(
                                                    groupId,
                                                );
                                            const toRemove = [
                                                entry.index,
                                                ...descendantIndices,
                                            ].sort((a, b) => b - a);
                                            remove(toRemove);
                                        }}
                                    >
                                        {removeLabel}
                                    </button>
                                </div>
                            ) : null}

                            <div className="mt-4 ml-1 border-l border-starkWhite pl-4 space-y-3">
                                {children.map(child =>
                                    renderGroupNode(child, depth + 1),
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h3 className="text-lg font-semibold">
                    {t('admin.forms.createOrganization.groupsTitle')}
                </h3>
                {hasHierarchyConstraint && !readOnly && (
                    <button
                        type="button"
                        className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1"
                        onClick={() =>
                            handleAddGroup({
                                depth: 0,
                                levelId: uniqueHierarchyLevelByDepth.get(0)?.id,
                            })
                        }
                    >
                        <TextIcon SVG={PlusIcon} />
                        <span>
                            {uniqueHierarchyLevelByDepth.get(0)?.name
                                ? t(
                                      'admin.forms.createOrganization.addGroupLevelNamed',
                                      {
                                          levelName:
                                              uniqueHierarchyLevelByDepth.get(0)
                                                  ?.name,
                                      },
                                  )
                                : t('admin.forms.createOrganization.addGroup')}
                        </span>
                    </button>
                )}
            </div>
            {fields.length === 0 && (
                <p className="text-sm text-mongooseGray">
                    {t('admin.forms.createOrganization.groupsEmpty')}
                </p>
            )}
            <div className="space-y-4">
                {rootNodes.map(node => renderGroupNode(node))}
            </div>
        </div>
    );
};
